<template>
  <div>
    <facility-new-dialog :state="newState" />
    <facility-assign-dialog :facility="selectedFacility" :state="assignState" />
    <div class="d-flex justify-space-between align-center pa-4 pa-md-2">
      <div>
        <Breadcrumbs headline="Cơ Sở Vật Chất" :link="[{ text: 'CSVC', href: '../facility' }]" />
      </div>
      <div>
        <v-btn
          v-if="['super_admin', 'education_manager'].includes(role.type)"
          color="primary"
          @click="newState = !newState"
        >
          <v-icon left>add</v-icon>Thêm
        </v-btn>
      </div>
    </div>

    <v-card class="pa-2 pa-md-2 mx-md-2">
      <v-row no-gutters>
        <v-col class="text-right pa-0">
          <span v-if="$vuetify.breakpoint.smAndDown">
            <facility-filter-dialog @onFilterChanged="refresh" />
          </span>
          <setting-table-header :default-headers="headers" @change="headers = $event" />
          <span v-if="$vuetify.breakpoint.mdAndUp">
            <kebap-menu>
              <v-list>
                <v-list-item>
                  <export-excel :custom-header="headers" api="/facilities/" />
                </v-list-item>
              </v-list>
            </kebap-menu>
          </span>
        </v-col>
      </v-row>
      <facility-filter
        @onFilterChanged="refresh"
        v-if="$vuetify.breakpoint.mdAndUp"
      ></facility-filter>
      <v-data-table
        :headers="headers"
        :items="facilities"
        :search="search"
        v-model="selected"
        :loading="loading"
        @click:row="onFacilitySelected"
        :items-per-page="5"
        select
        dense
      >
        <template v-slot:item.code="{ item }">
          <router-link :to="'/facility/' + item.id">{{ item.code }}</router-link>
        </template>
        <template v-slot:item.deadline="{ item }">
          <div class="nowrap">{{ item.deadline | familiarizeDate }}</div>
        </template>
        <template v-slot:item.date="{ item }">
          <div class="nowrap">{{ item.date | familiarizeDate }}</div>
        </template>
        <template v-slot:item.borrower="{ item }">
          <staff-name v-if="item.student" :staff="item.student" isStudent></staff-name>
          <staff-name v-if="item.teacher" :staff="item.teacher" isTeacher></staff-name>
          <staff-name v-if="item.staff" :staff="item.staff"></staff-name>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="getColor(item.status)" class="ma-2" label small dark>{{
            allFacilityStatus[item.status]
          }}</v-chip>
        </template>
        <template v-slot:item.type="{ item }">
          <v-chip small dark>{{ allFacilityType[item.type] }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row class="pa-2">
            <v-tooltip top v-if="item.status != 'using' && item.status != 'over-due'">
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on" @click.stop="onAssign(item)">pan_tool</v-icon>
              </template>
              <span>Cho Mượn</span>
            </v-tooltip>
            <v-tooltip top v-if="item.status === 'using' || item.status === 'over-due'">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" @click.stop="onReturnFacility(item)">history</v-icon>
              </template>
              <span>Hoàn Trả</span>
            </v-tooltip>
          </v-row>
        </template>
        <template
          v-slot:item.delete="{ item }"
          v-if="['super_admin', 'education_manager'].includes(role.type)"
        >
          <v-tooltip top v-if="item.status != 'using' && item.status != 'over-due'">
            <template v-slot:activator="{ on }">
              <v-icon class="mr-2" v-on="on" @click.stop="onRemove(item)">mdi-delete</v-icon>
            </template>
            <span>Xóa</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import FacilityFilter from '@/modules/facility/FacilityFilter'
import StaffName from '@/components/basic/card/CardStaffName'
import FacilityNewDialog from '@/modules/facility/FacilityNewDialog.vue'
import FacilityAssignDialog from '@/modules/facility/FacilityAssignDialog.vue'
import FacilityFilterDialog from '@/modules/facility/FacilityFilterDialog.vue'
import Breadcrumbs from '@/components/layout/Breadcrumbs'
import ExportExcel from '@/components/basic/ExportExcel'
import KebapMenu from '@/components/basic/menu/KebapMenu'
import SettingTableHeader from '@/components/basic/table/SettingHeaders'

export default {
  components: {
    FacilityFilter,
    FacilityNewDialog,
    FacilityAssignDialog,
    FacilityFilterDialog,
    StaffName,
    Breadcrumbs,
    ExportExcel,
    SettingTableHeader,
    KebapMenu
  },
  data() {
    return {
      search: '',
      expanded: [],
      loading: false,
      assignState: false,
      newState: false,
      selectedFacility: {},
      selected: [],
      headers: [
        { text: 'Mượn/Trả', value: 'actions', show: true },
        {
          text: 'Mã',
          align: 'left',
          sortable: false,
          value: 'code',
          show: true
        },
        { text: 'Tên', value: 'name', show: true },
        { text: 'Người mượn', value: 'borrower', show: true },
        { text: 'Ngày mượn', value: 'date', show: true },
        { text: 'Hạn trả', value: 'deadline', show: true },
        { text: 'Trạng thái', value: 'status', show: true },
        { text: 'Hành động', value: 'delete', show: true }
      ],
      draw: false,
      facilityId: ''
    }
  },
  computed: {
    ...mapGetters('facilities', ['facilities', 'facility']),
    ...mapState('constant', ['facilityStatus', 'facilityTypes']),
    ...mapGetters('app', ['department']),
    ...mapState('auth', ['profile', 'role']),
    allFacilityStatus() {
      return this.facilityStatus.reduce(
        (data, curent) => ({
          ...data,
          [curent.status]: curent['title']
        }),
        {}
      )
    },
    allFacilityType() {
      return this.facilityTypes.reduce(
        (data, curent) => ({
          ...data,
          [curent.status]: curent['title']
        }),
        {}
      )
    },
    facilityData() {
      return this.facility(this.facilityId)
    }
  },
  methods: {
    ...mapActions('facilities', [
      'fetchFacilities',
      'setFacility',
      'removeFacility',
      'updateFacility',
      'updateFacilities'
    ]),
    onFacilitySelected(facility) {},
    async onRemove(facility) {
      this.$dialog.confirm({
        title: 'Xóa CSVC',
        text: ` Xác nhận xóa cơ sở vật chất: ${facility.name}`,
        okText: 'Xóa',
        done: async () => {
          await this.removeFacility(facility.id)
        }
      })
    },
    onUpdate(filter) {},
    onAssign(facility) {
      this.selectedFacility = facility
      this.assignState = !this.assignState
    },
    onReturnFacility(facility) {
      this.$dialog.confirm({
        title: 'Xác Nhận Hoàn Trả CSVC',
        text: ` Xác nhận CSVC ${facility.name} đã được hoàn trả`,
        okText: 'Xác Nhận',
        cancelText: 'Hủy',
        done: async () => {
          await this.updateFacility({
            student: null,
            staff: null,
            teacher: null,
            deadline: null,
            date: null,
            'not-clear-request': true,
            status: 'inventory',
            id: facility.id
          })
        }
      })
    },
    async refresh(query) {
      this.loading = true
      await this.fetchFacilities({ ...query })
      this.loading = false
    },
    getColor(status) {
      if (status === 'inventory') return 'green'
      else if (status === 'over-due') return 'red'
      else if (status === 'opened') return 'blue'
      else if (status === 'paid') return 'gray'
      else if (status === 'using') return 'primary'
      else if (status === 'broken') return 'orange'
      else return 'gray'
    },
    async updateFacilityStatuses() {
      const overdueFacilities = this.facilities.filter(facility => {
        const deadline = new Date(facility.deadline).getTime()
        return facility.status === 'using' && deadline < new Date().getTime()
      })
      await this.updateFacilities(
        overdueFacilities.map(facility => ({
          id: facility.id,
          status: 'over-due'
        }))
      )
    }
  },
  async created() {
    if (this.facilities.length === 0) {
      await this.refresh('')
    }
    await this.updateFacilityStatuses()
  },
  filters: {
    familiarizeDate: time => {
      if (!time) {
        return ''
      }
      return moment(time).format('HH:mm DD/MM/YYYY')
    },
    SimplizedDate: time => {
      if (!time) {
        return ''
      }
      return moment(time).format('DD/MM')
    }
  }
}
</script>

To change the font size for a single component, such as a text field, application-wide:

<style scoped>
.v-select__level-sale {
  font-size: 1em;
  border: none;
}
.nowrap {
  white-space: nowrap;
}
.content {
  margin: 0 !important;
  padding: 0 !important;
  border: 0;
  outline: 0;
  font-size: 100%;
  vertical-align: baseline;
  background: transparent;
  width: 100%;
}
</style>
